import { HeartPulse } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { GRACKLE_COLORS, CATEGORICAL } from '@/lib/theme'
import type { AccountHealth } from '@/types'

// GRK-BRAND-5: score-number colour per band — palette + WCAG-safe on white.
function getHealthColor(score: number): {
  color: string
  badgeVariant: 'default' | 'secondary' | 'destructive' | 'outline'
} {
  if (score >= 80) return { color: CATEGORICAL.evergreen.solid, badgeVariant: 'secondary' }
  if (score >= 60) return { color: GRACKLE_COLORS.navy, badgeVariant: 'default' }
  if (score >= 40) return { color: CATEGORICAL.clay.solid, badgeVariant: 'outline' }
  if (score >= 20) return { color: CATEGORICAL.clay.solid, badgeVariant: 'outline' }
  return { color: GRACKLE_COLORS.darkRaspberry, badgeVariant: 'destructive' }
}

function getHealthLabel(score: number): string {
  if (score >= 80) return 'Healthy'
  if (score >= 60) return 'Good'
  if (score >= 40) return 'Attention'
  if (score >= 20) return 'At Risk'
  return 'Critical'
}

interface AccountHealthBadgeProps {
  score: number | null | undefined
  size?: 'sm' | 'md'
}

export function AccountHealthBadge({ score, size = 'sm' }: AccountHealthBadgeProps) {
  if (score == null) return null

  const { badgeVariant } = getHealthColor(score)
  const label = getHealthLabel(score)

  return (
    <Badge
      variant={badgeVariant}
      className={size === 'sm' ? 'text-[10px] px-1.5 py-0' : 'text-xs px-2 py-0.5'}
      title={`Account health: ${score}/100 - ${label}`}
      aria-label={`Account health: ${score} out of 100, ${label}`}
    >
      {score}
    </Badge>
  )
}

interface AccountHealthCardProps {
  health: AccountHealth | undefined
  isLoading?: boolean
}

export function AccountHealthCard({ health, isLoading }: AccountHealthCardProps) {
  if (isLoading) {
    return (
      <div className="text-center p-4" role="status">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    )
  }

  if (!health) return null

  const { color } = getHealthColor(health.score)
  const breakdownItems = [
    { key: 'engagement', label: 'Engagement' },
    { key: 'revenue', label: 'Revenue Health' },
    { key: 'renewal', label: 'Renewal Risk' },
    { key: 'breadth', label: 'Relationship Breadth' },
  ] as const

  return (
    <>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
        <CardTitle className="text-sm font-medium">Acct Health</CardTitle>
        <HeartPulse className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
      </CardHeader>
      <CardContent className="p-3">
        <div
          className="text-xl font-bold leading-none"
          style={{ color }}
          aria-label={`Account health score: ${health.score} out of 100, ${health.label}`}
        >
          {health.score}
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <button
              className="text-xs text-muted-foreground underline underline-offset-2 cursor-pointer hover:opacity-80 leading-tight"
              aria-label={`${health.label}. Click for score breakdown`}
            >
              {health.label} &mdash; Details
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3" align="start">
            <p className="text-xs font-semibold mb-2">Score Breakdown</p>
            <div className="space-y-1.5">
              {breakdownItems.map(({ key, label }) => {
                const item = health.breakdown[key]
                return (
                  <div key={key} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{label}</span>
                    <span
                      className="font-medium"
                      title={item.detail}
                      aria-label={`${label}: ${item.score} of ${item.max}. ${item.detail}`}
                    >
                      {item.score}/{item.max}
                    </span>
                  </div>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      </CardContent>
    </>
  )
}
